import React, { useMemo, useState } from 'react';

import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import lockSvg from '@assets/img/icons/lock.svg';
import unlockSvg from '@assets/img/icons/unlock.svg';
import { PaymentSlipFormDialog } from '@components/admin/booking/PaymentSlipFormDialog';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { paymentSlipStrings } from '@data/paymentSlip';
import { useSearch } from '@hooks/next/useSearch';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUserHelper } from '@hooks/queries/useUserHelper';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { PaymentSlipHelper, PaymentSlipPutAction } from '@models/next/PaymentSlip';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useMutation } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { toJST } from '@utils/types';
import { stringify } from 'query-string';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  title: {
    title: `${paymentSlipStrings.payment_slip}`,
    exportPdf: 'PDF出力',
    unlock: 'ロック解除',
    confirmAndSubmit: '確定する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationFloat: {
    position: 'fixed',
    bottom: 0,
    width: '100%',
    background: theme.palette.primary.contrastText,
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
    padding: '10px 45px',
  },
  operationInner: {
    maxWidth: 900,
    margin: '0 auto',
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.middleText,
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px',
    width: '100%',
  },
  info: {
    flex: 1,
    ...common.flexColumn,
  },
  operation: {
    ...common.flexCenter,
    columnGap: 30,
  },
}));

export const PaymentSlipDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(
    z.object({
      id: z.number(),
      d2d_no: z.string(),
    }),
  );

  const [openEdit, setOpenEdit] = useState(false);

  const editDialogKey = useDialogKey(openEdit);

  const confirmDialog = useConfirmDialog();

  const userHelper = useUserHelper();

  const bookingInquiry = useBookingInquiryDetailByD2dNo(search.d2d_no);

  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: search.d2d_no,
    },
  });

  const paymentSlipQuery = dashboardPaymentSlipApis.useGet('/dashboard/payment-slip/:id', {
    params: {
      id: search.id,
    },
  });

  const paymentSlipPdfQuery = dashboardPdfApis.useGet(
    '/dashboard/pdf/PaymentSlip/:id',
    {
      params: {
        id: search.id,
      },
    },
    {
      staleTime: Infinity,
    },
  );

  const putPaymentSlip = useMutation(
    async (action: PaymentSlipPutAction) => {
      await dashboardPaymentSlipApis.client.put('/dashboard/payment-slip/:id', undefined, {
        params: { id: search.id },
        queries: { action },
      });
    },
    {
      onSuccess: () => paymentSlipQuery.invalidate(),
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [putPaymentSlip] });

  const pdfUrl = useMemo(
    () => (paymentSlipPdfQuery.data ? URL.createObjectURL(paymentSlipPdfQuery.data) : undefined),
    [paymentSlipPdfQuery.data],
  );

  const canUpdate =
    userHelper.data &&
    preAlert.data &&
    paymentSlipQuery.data &&
    PaymentSlipHelper.canUpdatePaymentSlip(
      userHelper.data.user,
      preAlert.data,
      paymentSlipQuery.data,
      bookingInquiry.data,
    );

  const renderOperationPanel = () => {
    const item = paymentSlipQuery.data;

    if (!item) return null;

    const lastStatus = item.status.at(-1);

    return (
      <div className={classes.operationFloat}>
        <div className={classes.operationInner}>
          {renderAlertsAndSpinnerModal()}
          <div className={classes.operationPanel}>
            <div className={classes.operationBox}>
              <div className={classes.info}>
                <div>
                  {paymentSlipStrings.created_at}
                  {': '}
                  {toJST(new Date(item.created_at))}
                </div>

                <div>
                  {paymentSlipStrings.creator}
                  {': '}
                  {new UserHelper(item.creator).fullname ?? '-'}
                </div>

                <div>Latest Update: {toJST(new Date(item.updated_at))}</div>
                <div>
                  <a
                    className={classes.link}
                    href={
                      url('/admin/booking/PrintPaymentSlipLog') + '?' + stringify({ id: search.id })
                    }
                    target="_blank"
                  >
                    {paymentSlipStrings.change_log}
                  </a>
                </div>
              </div>

              <div className={classes.operation}>
                <Button
                  color="primary"
                  text={strings.exportPdf}
                  type="button"
                  shape="round"
                  size="small"
                  onClick={async () => {
                    if (pdfUrl) {
                      window.open(pdfUrl);
                    }
                  }}
                />

                {(lastStatus?.name === '下書き' || lastStatus?.name === '修正中' || !lastStatus) &&
                  canUpdate && (
                    <>
                      <Button
                        color={foldMessagesEither(
                          canUpdate,
                          () => 'grey',
                          () => 'primary',
                        )}
                        text={formStrings.edit}
                        type="button"
                        shape="round"
                        size="small"
                        onClick={() => {
                          if (canUpdate._tag === 'Left') {
                            confirmDialog.confirm({
                              message: canUpdate.left.messages.join('\n'),
                            });
                          } else {
                            setOpenEdit(true);
                          }
                        }}
                      />

                      <Button
                        color="primary"
                        text={
                          <>
                            <img src={lockSvg} alt="Lock" />
                            {strings.confirmAndSubmit}
                          </>
                        }
                        type="button"
                        shape="round"
                        size="small"
                        onClick={async () => {
                          if (canUpdate._tag === 'Left') {
                            confirmDialog.confirm({
                              message: canUpdate.left.messages.join('\n'),
                            });
                          } else {
                            if (
                              (await confirmDialog.confirm({
                                message: paymentSlipStrings.warningSend,
                              })) === 'confirmed'
                            ) {
                              await putPaymentSlip.mutateAsync('SEND');
                            }
                          }
                        }}
                      />
                    </>
                  )}

                {lastStatus?.name === '作成済' && (
                  <Button
                    color="warning"
                    text={
                      <>
                        <img src={unlockSvg} alt="Unlock" />
                        {strings.unlock}
                      </>
                    }
                    shape="round"
                    size="small"
                    onClick={async () => {
                      foldMessagesEither(
                        PaymentSlipHelper.canUnlockPaymentSlip(
                          userHelper.data!.user,
                          preAlert.data!,
                          paymentSlipQuery.data!,
                        ),
                        async (e) => {
                          await confirmDialog.confirm({ message: e.messages });
                        },
                        async () => {
                          if (
                            (await confirmDialog.confirm({
                              message: paymentSlipStrings.warningAdminUnlock,
                            })) === 'confirmed'
                          ) {
                            await putPaymentSlip.mutateAsync('UNLOCK');
                          }
                        },
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={strings.title}
      loading={
        paymentSlipQuery.isLoading ||
        paymentSlipPdfQuery.isLoading ||
        bookingInquiry.isLoading ||
        preAlert.isLoading
      }
      errors={[
        paymentSlipQuery.error,
        paymentSlipPdfQuery.error,
        bookingInquiry.error,
        preAlert.error,
      ]}
      classes={{
        background: classes.background,
      }}
    >
      <div className={classes.container}>
        <Document key={pdfUrl} file={pdfUrl} loading="">
          <Page pageNumber={1} width={900} />
        </Document>
      </div>
      {renderOperationPanel()}

      {bookingInquiry.data && preAlert.data && paymentSlipQuery.data && (
        <PaymentSlipFormDialog
          key={editDialogKey}
          type="update"
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          bookingInquiry={bookingInquiry.data}
          preAlert={preAlert.data}
          paymentSlip={paymentSlipQuery.data}
        />
      )}

      {confirmDialog.renderDialog()}
    </Layout>
  );
};
